import dayjs from "dayjs";
import type { Sort } from "element-plus";
import { useRoute, useRouter } from "vue-router";
import {
  Shortcut,
  useDateShortcuts,
  type DateRangeFilter,
} from "./dateShortcuts";

const INVALID_DATE = "Invalid Date";

export default function useRouteQueryParams() {
  const route = useRoute();
  const router = useRouter();
  const { getShortcutDateRange, defaultShortcut } = useDateShortcuts();

  type QueryKey =
    | "category"
    | "date"
    | "department"
    | "from"
    | "role"
    | "search"
    | "sort"
    | "space"
    | "status"
    | "to"
    | "user";

  const setQueryParam = async (
    key: QueryKey,
    value: string,
    removeValue: string
  ) => {
    value === removeValue
      ? await clearParameters([key])
      : await addParameter(key, value);
  };

  const setDateQueryParam = async (filter: DateRangeFilter) => {
    await clearParameters(["date", "from", "to"]);

    if (filter.shortcut === Shortcut.CUSTOM) {
      await setQueryParam(
        "from",
        dayjs(filter.dateRange[0]).format("YYYY-MM-DD"),
        ""
      );
      await setQueryParam(
        "to",
        dayjs(filter.dateRange[1]).format("YYYY-MM-DD"),
        ""
      );
    } else {
      await setQueryParam("date", filter.shortcut, Shortcut.LAST_7_DAYS);
    }
  };

  const addParameter = async (key: QueryKey, value: string) => {
    const newQuery = {
      ...route.query,
      [key]: value,
    };

    await router.replace({
      ...router.currentRoute.value,
      query: newQuery,
    });
  };

  const clearParameters = async (filterNames: QueryKey[]) => {
    const currentQuery = { ...router.currentRoute.value.query };

    filterNames.forEach((filterName) => {
      delete currentQuery[filterName];
    });

    await router.replace({
      ...router.currentRoute.value,
      query: currentQuery,
    });
  };

  const getQueryParam = (key: QueryKey, defaultValue: string): string => {
    const queryValue = route.query[key];

    if (queryValue) {
      if (Array.isArray(queryValue)) {
        return queryValue[0] ? queryValue[0].toString() : defaultValue;
      }
      return queryValue.toString();
    }
    return defaultValue;
  };

  const queryParamToDateFilter = (
    defaultShortcut?: Shortcut
  ): DateRangeFilter => {
    const startDate = dayjs(getQueryParam("from", "")).toDate();
    const endDate = dayjs(getQueryParam("to", "")).toDate();

    if (
      startDate.toString() !== INVALID_DATE &&
      endDate.toString() !== INVALID_DATE
    ) {
      return {
        shortcut: Shortcut.CUSTOM,
        dateRange: [startDate, endDate],
      };
    }

    const shortcut = stringToShortcut(
      getQueryParam("date", ""),
      defaultShortcut
    );

    return {
      shortcut,
      dateRange: getShortcutDateRange(shortcut),
    };
  };

  const DateRangeFilterToDateTuple = (
    filter: DateRangeFilter
  ): [Date, Date] => {
    if (filter.shortcut === Shortcut.CUSTOM) {
      return [
        dayjs(filter.dateRange[0]).startOf("day").toDate(),
        dayjs(filter.dateRange[1]).endOf("day").toDate(),
      ];
    }

    return getShortcutDateRange(filter.shortcut);
  };

  const queryParamToSort = (
    defaultField: string,
    defaultOrder: "asc" | "desc"
  ): Sort => {
    const sort = getQueryParam("sort", "");

    let field = defaultField;
    let order = defaultOrder;

    if (sort.endsWith("asc")) {
      field = sort.slice(0, -4);
      order = "asc";
    } else if (sort.endsWith("desc")) {
      field = sort.slice(0, -5);
      order = "desc";
    }

    return {
      prop: field,
      order: order === "asc" ? "ascending" : "descending",
    };
  };

  const stringToShortcut = (
    value: string,
    defaultShortcut?: Shortcut
  ): Shortcut => {
    if (
      (Object.values(Shortcut) as string[]).includes(value) &&
      value.toLowerCase() !== Shortcut.CUSTOM
    ) {
      return value as Shortcut;
    }
    return defaultShortcut ?? Shortcut.LAST_7_DAYS;
  };

  const isDateRangeChanged = (filter: DateRangeFilter) => {
    return filter.shortcut !== defaultShortcut.value;
  };

  return {
    setQueryParam,
    setDateQueryParam,
    getQueryParam,
    queryParamToDateFilter,
    DateRangeFilterToDateTuple,
    queryParamToSort,
    isDateRangeChanged,
  };
}
